import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { Button, EmptyState, Field, SegmentedControl, inputStyle, tokens } from './ui/controls';

type RegistryDataset = { dataset_id: string; name: string; version: string };
type ClassCount = { name: string; boxes: number; rows: number };
type Summary = { rows: number; images: number; classes: ClassCount[] };

type DatasetEntry = { key: string; name: string; version: string; raw: Record<string, unknown> };

function readDatasets(spec: Record<string, unknown>): DatasetEntry[] {
  const raw = spec.datasets;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>).flatMap(([key, value]) => {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) return [];
    const row = value as Record<string, unknown>;
    const name = String(row.name || '');
    const version = String(row.version || '');
    if (!name || !version) return [];
    return [{ key, name, version, raw: row }];
  });
}

function readRemapping(spec: Record<string, unknown>): Record<string, unknown> {
  const conversion = spec.label_conversion;
  if (conversion == null || typeof conversion !== 'object' || Array.isArray(conversion)) return {};
  const remapping = (conversion as Record<string, unknown>).label_remapping;
  if (remapping == null || typeof remapping !== 'object' || Array.isArray(remapping)) return {};
  return remapping as Record<string, unknown>;
}

function readStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item)).filter(Boolean);
}

function countText(row: ClassCount) {
  if (row.boxes > 0) return `${row.boxes.toLocaleString()} boxes`;
  return `${row.rows.toLocaleString()} rows`;
}

function writeDatasets(spec: Record<string, unknown>, entries: DatasetEntry[]) {
  const datasets: Record<string, unknown> = {};
  entries.forEach((entry) => {
    datasets[entry.key] = { ...entry.raw, mode: entry.raw.mode || 'sql', name: entry.name, version: entry.version };
  });
  return { ...spec, datasets };
}

function readMapping(spec: Record<string, unknown>): Record<string, string> {
  const raw = readRemapping(spec).class_name_mapping;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const mapping: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim()) mapping[key] = value.trim();
  });
  return mapping;
}

function mappedName(mapping: Record<string, string>, name: string) {
  return mapping[name] || name;
}

function writeRemap(
  spec: Record<string, unknown>,
  enabled: boolean,
  deleted: string[],
  order: string[],
  mapping: Record<string, string>,
) {
  const conversion = spec.label_conversion;
  const base = conversion != null && typeof conversion === 'object' && !Array.isArray(conversion)
    ? { ...(conversion as Record<string, unknown>) }
    : {};
  const remapping = readRemapping(spec);
  return {
    ...spec,
    label_conversion: {
      ...base,
      label_remapping: {
        ...remapping,
        enabled,
        deleted_labels: deleted,
        expected_class_order: order,
        class_name_mapping: mapping,
      },
    },
  };
}

export function JobDatasets({
  spec,
  onChange,
}: {
  spec: Record<string, unknown>;
  onChange: (spec: Record<string, unknown>) => void;
}) {
  const entries = readDatasets(spec);
  const deleted = readStringList(readRemapping(spec).deleted_labels);
  const [registry, setRegistry] = useState<RegistryDataset[]>([]);
  const [registryError, setRegistryError] = useState('');
  const [pickedName, setPickedName] = useState('');
  const [pickedVersion, setPickedVersion] = useState('');
  const [summaries, setSummaries] = useState<Record<string, Summary | { error: string }>>({});
  const requested = useRef(new Set<string>());
  const entryKey = entries.map((entry) => `${entry.name}@${entry.version}`).join('\n');

  useEffect(() => {
    api.dataviewer.listRegistryDatasets()
      .then((res) => {
        setRegistry(res.data.datasets || []);
        setRegistryError('');
      })
      .catch((err: any) => setRegistryError(err.response?.data?.detail || err.message || 'Could not list datasets'));
  }, []);

  const includedIds = useMemo(() => new Set(entries.map((entry) => `${entry.name}@${entry.version}`)), [entries]);

  useEffect(() => {
    entries.forEach((entry) => {
      const id = `${entry.name}@${entry.version}`;
      if (requested.current.has(id)) return;
      requested.current.add(id);
      api.dataviewer.datasetSummary(entry.name, entry.version)
        .then((res) => {
          setSummaries((prev) => ({
            ...prev,
            [id]: { rows: res.data.rows, images: res.data.images, classes: res.data.classes },
          }));
        })
        .catch((err: any) => {
          setSummaries((prev) => ({
            ...prev,
            [id]: { error: err.response?.data?.detail || err.message || 'Summary failed' },
          }));
        });
    });
  }, [entryKey, entries]);

  const loaded = entries.flatMap((entry) => {
    const summary = summaries[`${entry.name}@${entry.version}`];
    if (!summary || 'error' in summary) return [];
    return [{ entry, summary }];
  });

  const classTotals = new Map<string, ClassCount>();
  loaded.forEach(({ summary }) => {
    summary.classes.forEach((row) => {
      const current = classTotals.get(row.name) || { name: row.name, boxes: 0, rows: 0 };
      classTotals.set(row.name, {
        name: row.name,
        boxes: current.boxes + row.boxes,
        rows: current.rows + row.rows,
      });
    });
  });
  const classes = Array.from(classTotals.values()).sort((a, b) => a.name.localeCompare(b.name));
  const totalRows = loaded.reduce((sum, item) => sum + item.summary.rows, 0);
  const totalImages = loaded.reduce((sum, item) => sum + item.summary.images, 0);

  const available = registry.filter((row) => !includedIds.has(row.dataset_id));
  const names = Array.from(new Set(available.map((row) => row.name))).sort((a, b) => a.localeCompare(b));
  const versions = available
    .filter((row) => row.name === pickedName)
    .map((row) => row.version)
    .sort((a, b) => a.localeCompare(b));

  const addDataset = () => {
    const row = available.find((item) => item.name === pickedName && item.version === pickedVersion);
    if (!row) return;
    const keyBase = row.name.replace(/[^A-Za-z0-9_]/g, '_');
    const used = new Set(entries.map((entry) => entry.key));
    let key = keyBase || 'dataset';
    if (used.has(key)) key = `${key}_${row.version.replace(/[^A-Za-z0-9_]/g, '_')}`;
    onChange(writeDatasets(spec, [...entries, { key, name: row.name, version: row.version, raw: { mode: 'sql', name: row.name, version: row.version } }]));
    setPickedName('');
    setPickedVersion('');
  };

  const mapping = readMapping(spec);
  const remapEnabled = Boolean(readRemapping(spec).enabled);

  const orderFor = (nextDeleted: string[], nextMapping: Record<string, string>) => {
    const kept = classes
      .filter((row) => !nextDeleted.includes(row.name))
      .map((row) => mappedName(nextMapping, row.name));
    const unique = kept.filter((name, index) => kept.indexOf(name) === index);
    const previous = readStringList(readRemapping(spec).expected_class_order);
    const still = previous.filter((name) => unique.includes(name));
    const added = unique.filter((name) => !still.includes(name));
    return previous.length === 0 && still.length === 0 ? unique : [...still, ...added];
  };

  const toggleLabel = (name: string, include: boolean) => {
    const nextDeleted = include
      ? deleted.filter((label) => label !== name)
      : (deleted.includes(name) ? deleted : [...deleted, name]);
    onChange(writeRemap(spec, remapEnabled, nextDeleted, orderFor(nextDeleted, mapping), mapping));
  };

  const setMapped = (name: string, value: string) => {
    const next = { ...mapping };
    delete next[name];
    const trimmed = value.trim();
    if (trimmed && trimmed !== name) next[name] = trimmed;
    onChange(writeRemap(spec, remapEnabled, deleted, orderFor(deleted, next), next));
  };

  return (
    <div style={{ display: 'grid', gap: '0.85rem' }}>
      <h3 style={{ margin: 0, fontSize: '1rem' }}>Datasets in this job</h3>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
        <Field label="Dataset">
          <select
            value={pickedName}
            onChange={(e) => {
              setPickedName(e.target.value);
              setPickedVersion('');
            }}
            style={{ ...inputStyle, minWidth: 220 }}
          >
            <option value="">{names.length ? 'Choose a dataset' : 'No more datasets'}</option>
            {names.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </Field>
        <Field label="Version">
          <select
            value={pickedVersion}
            onChange={(e) => setPickedVersion(e.target.value)}
            disabled={!pickedName}
            style={{ ...inputStyle, minWidth: 140 }}
          >
            <option value="">{pickedName ? 'Choose a version' : 'Choose a dataset first'}</option>
            {versions.map((version) => (
              <option key={version} value={version}>{version}</option>
            ))}
          </select>
        </Field>
        <Button onClick={addDataset} disabled={!pickedName || !pickedVersion}>Add</Button>
      </div>
      {registryError && <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{registryError}</span>}

      {entries.length === 0 ? (
        <EmptyState>No datasets in this job. Load a template or add one from the registry.</EmptyState>
      ) : (
        <>
          <div style={{ fontSize: '0.85rem', color: tokens.textMuted }}>
            {entries.length} dataset{entries.length === 1 ? '' : 's'}
            {loaded.length > 0 && ` · ${totalRows.toLocaleString()} rows · ${totalImages.toLocaleString()} images`}
            {loaded.length < entries.length && ' · waiting on summaries'}
          </div>
          {classes.length > 0 && (
            <div style={{ display: 'grid', gap: '0.45rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Label remap</span>
                <SegmentedControl
                  value={remapEnabled ? 'on' : 'off'}
                  onChange={(id) => onChange(writeRemap(spec, id === 'on', deleted, orderFor(deleted, mapping), mapping))}
                  options={[{ id: 'off', label: 'Off' }, { id: 'on', label: 'On' }]}
                />
              </div>
              {classes.map((row) => {
                const included = !deleted.includes(row.name);
                return (
                  <div key={row.name} style={{ display: 'flex', gap: '0.55rem', alignItems: 'center', fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={included}
                      aria-label={`Include ${row.name}`}
                      onChange={(e) => toggleLabel(row.name, e.target.checked)}
                    />
                    <span style={{ minWidth: 120 }}>{row.name}</span>
                    <span style={{ color: tokens.textMuted, minWidth: 90 }}>{countText(row)}</span>
                    <input
                      aria-label={`Train ${row.name} as`}
                      value={mappedName(mapping, row.name)}
                      onChange={(e) => setMapped(row.name, e.target.value)}
                      disabled={!included}
                      style={{ ...inputStyle, width: 160, marginLeft: 'auto' }}
                    />
                  </div>
                );
              })}
              <span style={{ fontSize: '0.78rem', color: tokens.textFaint }}>
                Turn remap on to drop unchecked labels and train the rest under the name in each box.
              </span>
            </div>
          )}
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {entries.map((entry) => {
              const id = `${entry.name}@${entry.version}`;
              const summary = summaries[id];
              return (
                <div
                  key={entry.key}
                  style={{
                    border: `1px solid ${tokens.border}`,
                    borderRadius: tokens.radius,
                    padding: '0.75rem 0.9rem',
                    display: 'grid',
                    gap: '0.35rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <strong style={{ fontSize: '0.95rem' }}>{id}</strong>
                    <Button
                      variant="subtle"
                      onClick={() => onChange(writeDatasets(spec, entries.filter((item) => item.key !== entry.key)))}
                      style={{ marginLeft: 'auto', fontSize: '0.8rem', padding: '0.25rem 0.55rem' }}
                    >
                      Remove
                    </Button>
                  </div>
                  {!summary && <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>Loading summary…</span>}
                  {summary && 'error' in summary && (
                    <span style={{ fontSize: '0.82rem', color: tokens.danger }}>{summary.error}</span>
                  )}
                  {summary && !('error' in summary) && (
                    <>
                      <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
                        {summary.rows.toLocaleString()} rows · {summary.images.toLocaleString()} images
                      </span>
                      {summary.classes.length === 0 ? (
                        <span style={{ fontSize: '0.82rem', color: tokens.textFaint }}>No labels in this dataset.</span>
                      ) : (
                        <span style={{ fontSize: '0.82rem', color: tokens.textMuted }}>
                          {summary.classes.map((row) => `${row.name} (${countText(row)})`).join(' · ')}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
